import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import { listDocumentTypes } from '../../../services/hr/documentTypesApi';
import Modal from '../../../components/Modal/Modal';

import {
   Section,
   SectionHeader,
   SectionBody,
   SectionTitle,
   SectionActions,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import { EmptyState } from '../../../styles/feedback/EmptyState';

// ─── Mapeamentos de exibição ───────────────────────────────────────────────────

// Fallback de LINHAS LEGADAS (handoff 0065 §7): o tipo virou catálogo
// relacional (GET /hr/document-types?scope=EMPLOYEE) e a resposta traz
// `document_type_name`; este mapa só cobre registros antigos sem o nome.
const DOCUMENT_TYPE_LABEL = {
   CONTRATO_EXPERIENCIA: 'Contrato de experiência',
   CONTRATO_EFETIVO: 'Contrato efetivo',
   TERMO_RESPONSABILIDADE: 'Termo de responsabilidade',
   ADVERTENCIA_FORMAL: 'Advertência formal',
   SUSPENSAO_FORMAL: 'Suspensão formal',
   RESCISAO: 'Rescisão',
   OUTROS: 'Outros',
};

// Tipos aceitos pelo backend: PDF, JPEG, PNG, WEBP — limite 10MB
const ACCEPTED_MIME_TYPES = 'application/pdf,image/jpeg,image/png,image/webp';
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSizeDisplay(bytes) {
   if (!bytes) return '—';
   if (bytes < 1024) return `${bytes} B`;
   if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
   return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month, day] = String(isoDateString).substring(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

// ─── Estado inicial do formulário de upload ───────────────────────────────────

const EMPTY_UPLOAD_FORM_STATE = {
   document_type_id: '',
   document_title: '',
   document_date: '',
   is_confidential: false,
   notes: '',
};

// ─── Componente principal ─────────────────────────────────────────────────────

export default function EmployeeDocumentsSection({ employeeId }) {
   const [documents, setDocuments] = useState([]);
   const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

   // ─── Modal de upload ──────────────────────────────────────────
   const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
   const [uploadFormData, setUploadFormData] = useState(
      EMPTY_UPLOAD_FORM_STATE
   );
   const [selectedFile, setSelectedFile] = useState(null);
   const [isUploading, setIsUploading] = useState(false);
   const fileInputRef = useRef(null);

   // Catálogo de tipos (scope=EMPLOYEE) — carregado uma vez, no primeiro open
   // do modal de upload (handoff 0065 §7).
   const [documentTypes, setDocumentTypes] = useState([]);
   const documentTypesLoadedRef = useRef(false);

   // ─── Modal de confirmação de exclusão ─────────────────────────
   const [documentPendingDeletion, setDocumentPendingDeletion] = useState(null);
   const [isDeleting, setIsDeleting] = useState(false);

   // ─── Busca de documentos ──────────────────────────────────────

   async function fetchDocuments() {
      setIsLoadingDocuments(true);
      try {
         const response = await axios.get(
            `/hr/employees/${employeeId}/documents`
         );
         setDocuments(response.data?.data?.documents ?? []);
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao carregar documentos.';
         toast.error(errorMessage);
      } finally {
         setIsLoadingDocuments(false);
      }
   }

   useEffect(() => {
      fetchDocuments();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [employeeId]);

   // ─── Handlers do formulário de upload ────────────────────────

   function handleUploadFormFieldChange(event) {
      const { name, value, type, checked } = event.target;
      setUploadFormData((previousFormData) => ({
         ...previousFormData,
         [name]: type === 'checkbox' ? checked : value,
      }));
   }

   function handleFileInputChange(event) {
      const file = event.target.files?.[0] ?? null;
      if (!file) return;

      if (file.size > MAX_FILE_SIZE_BYTES) {
         toast.error('Arquivo muito grande. O limite é 10MB.');
         event.target.value = '';
         return;
      }

      setSelectedFile(file);
   }

   function handleOpenUploadModal() {
      setUploadFormData(EMPTY_UPLOAD_FORM_STATE);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploadModalOpen(true);

      if (!documentTypesLoadedRef.current) {
         listDocumentTypes({ scope: 'EMPLOYEE' })
            .then((types) => {
               documentTypesLoadedRef.current = true;
               setDocumentTypes(types);
            })
            .catch(() => {
               toast.error(
                  'Erro ao carregar os tipos de documento. Reabra o modal.'
               );
            });
      }
   }

   const handleCloseUploadModal = useCallback(() => {
      setIsUploadModalOpen(false);
   }, []);
   async function handleUploadFormSubmit() {
      if (!uploadFormData.document_type_id) {
         toast.error('Tipo de documento é obrigatório.');
         return;
      }
      if (!uploadFormData.document_title.trim()) {
         toast.error('Título do documento é obrigatório.');
         return;
      }
      if (!selectedFile) {
         toast.error('Selecione um arquivo.');
         return;
      }

      setIsUploading(true);
      try {
         // Upload via multipart/form-data — não pode usar JSON
         const multipartFormData = new FormData();
         multipartFormData.append('file', selectedFile);
         multipartFormData.append(
            'document_type_id',
            uploadFormData.document_type_id
         );
         multipartFormData.append(
            'document_title',
            uploadFormData.document_title
         );
         if (uploadFormData.document_date) {
            multipartFormData.append(
               'document_date',
               uploadFormData.document_date
            );
         }
         multipartFormData.append(
            'is_confidential',
            uploadFormData.is_confidential ? '1' : '0'
         );
         if (uploadFormData.notes.trim()) {
            multipartFormData.append('notes', uploadFormData.notes.trim());
         }

         await axios.post(
            `/hr/employees/${employeeId}/documents`,
            multipartFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
         );

         toast.success('Documento enviado com sucesso!');
         setIsUploadModalOpen(false);
         fetchDocuments();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao enviar documento.';
         toast.error(errorMessage);
      } finally {
         setIsUploading(false);
      }
   }

   // ─── Handler de download ──────────────────────────────────────

   async function handleDownloadDocument(documentId, documentOriginalName) {
      try {
         const response = await axios.get(
            `/hr/employees/${employeeId}/documents/${documentId}/download`
         );
         const presignedDownloadUrl = response.data?.data?.download_url;

         if (!presignedDownloadUrl) {
            toast.error('Não foi possível gerar o link de download.');
            return;
         }

         // Abre a presigned URL diretamente em nova aba
         window.open(presignedDownloadUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao gerar link de download.';
         toast.error(errorMessage);
      }
   }

   // ─── Handlers de exclusão ─────────────────────────────────────

   function handleRequestDeleteDocument(document) {
      setDocumentPendingDeletion(document);
   }

   const handleCancelDeleteDocument = useCallback(() => {
      setDocumentPendingDeletion(null);
   }, []);

   async function handleConfirmDeleteDocument() {
      if (!documentPendingDeletion) return;

      setIsDeleting(true);
      try {
         await axios.delete(
            `/hr/employees/${employeeId}/documents/${documentPendingDeletion.id}`
         );
         toast.success('Documento excluído com sucesso.');
         setDocumentPendingDeletion(null);
         fetchDocuments();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao excluir documento.';
         toast.error(errorMessage);
      } finally {
         setIsDeleting(false);
      }
   }

   // ─── Render ──────────────────────────────────────────────────

   return (
      <>
         {/* ─── Seção principal ──────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Documentos</SectionTitle>
               <SectionActions>
                  <button
                     type="button"
                     onClick={handleOpenUploadModal}
                     disabled={isLoadingDocuments}
                  >
                     + Novo documento
                  </button>
               </SectionActions>
            </SectionHeader>
            <SectionBody>
               {isLoadingDocuments ? (
                  <p
                     style={{
                        color: '#9ca3af',
                        fontSize: '0.875rem',
                        margin: 0,
                     }}
                  >
                     Carregando documentos...
                  </p>
               ) : documents.length > 0 ? (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th>Tipo</th>
                              <th>Título</th>
                              <th>Arquivo</th>
                              <th>Tamanho</th>
                              <th>Data doc.</th>
                              <th>Enviado em</th>
                              <th>Enviado por</th>
                              <th>Conf.</th>
                              <th>Ações</th>
                           </tr>
                        </thead>
                        <tbody>
                           {documents.map((document) => (
                              <tr key={document.id}>
                                 <td>
                                    {document.document_type_name ??
                                       DOCUMENT_TYPE_LABEL[
                                          document.document_type
                                       ] ??
                                       document.document_type}
                                 </td>
                                 <td>{document.document_title}</td>
                                 <td>{document.file_original_name}</td>
                                 <td style={{ textAlign: 'right' }}>
                                    {formatFileSizeDisplay(
                                       document.file_size_bytes
                                    )}
                                 </td>
                                 <td style={{ textAlign: 'center' }}>
                                    {formatDateBR(document.document_date)}
                                 </td>
                                 <td style={{ textAlign: 'center' }}>
                                    {formatDateBR(document.created_at)}
                                 </td>
                                 <td>{document.uploadedByUser?.nome ?? '—'}</td>
                                 <td style={{ textAlign: 'center' }}>
                                    {document.is_confidential ? '🔒' : '—'}
                                 </td>
                                 <td
                                    style={{
                                       textAlign: 'center',
                                       whiteSpace: 'nowrap',
                                    }}
                                 >
                                    <button
                                       type="button"
                                       onClick={() =>
                                          handleDownloadDocument(
                                             document.id,
                                             document.file_original_name
                                          )
                                       }
                                       style={{ marginRight: '8px' }}
                                    >
                                       Baixar
                                    </button>
                                    <button
                                       type="button"
                                       onClick={() =>
                                          handleRequestDeleteDocument(document)
                                       }
                                    >
                                       Excluir
                                    </button>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               ) : (
                  <EmptyState
                     title="Nenhum documento cadastrado"
                     description="Clique em '+ Novo documento' para fazer o primeiro upload."
                  />
               )}
            </SectionBody>
         </Section>

         {/* ─── Modal de upload ──────────────────────────────── */}
         <Modal
            isOpen={isUploadModalOpen}
            title="Enviar documento"
            onClose={handleCloseUploadModal}
            maxWidth="600px"
         >
            <FormGrid $cols={12} $colsMobile={12}>
               <Field $col={6} $colMobile={12}>
                  <label htmlFor="upload-document-type">Tipo *</label>
                  <select
                     id="upload-document-type"
                     name="document_type_id"
                     value={uploadFormData.document_type_id}
                     onChange={handleUploadFormFieldChange}
                  >
                     <option value="">
                        {documentTypes.length
                           ? 'Selecione...'
                           : 'Carregando tipos…'}
                     </option>
                     {documentTypes.map((documentType) => (
                        <option key={documentType.id} value={documentType.id}>
                           {documentType.name}
                        </option>
                     ))}
                  </select>
               </Field>

               <Field $col={6} $colMobile={12}>
                  <label htmlFor="upload-document-date">
                     Data do documento
                  </label>
                  <input
                     id="upload-document-date"
                     type="date"
                     name="document_date"
                     value={uploadFormData.document_date}
                     onChange={handleUploadFormFieldChange}
                  />
               </Field>

               <Field $col={12} $colMobile={12}>
                  <label htmlFor="upload-document-title">Título *</label>
                  <input
                     id="upload-document-title"
                     type="text"
                     name="document_title"
                     value={uploadFormData.document_title}
                     onChange={handleUploadFormFieldChange}
                     placeholder="Ex: Contrato de experiência — admissão jan/2026"
                  />
               </Field>

               <Field $col={12} $colMobile={12}>
                  <label htmlFor="upload-file">
                     Arquivo * (PDF, JPEG, PNG, WEBP — máx. 10MB)
                  </label>
                  <input
                     id="upload-file"
                     type="file"
                     ref={fileInputRef}
                     accept={ACCEPTED_MIME_TYPES}
                     onChange={handleFileInputChange}
                  />
                  {selectedFile && (
                     <p
                        style={{
                           margin: '4px 0 0',
                           fontSize: '0.75rem',
                           color: '#6b7280',
                        }}
                     >
                        {selectedFile.name} —{' '}
                        {formatFileSizeDisplay(selectedFile.size)}
                     </p>
                  )}
               </Field>

               <Field $col={12} $colMobile={12}>
                  <label htmlFor="upload-notes">Observações</label>
                  <textarea
                     id="upload-notes"
                     name="notes"
                     value={uploadFormData.notes}
                     onChange={handleUploadFormFieldChange}
                     placeholder="Observações adicionais sobre o documento..."
                     rows={3}
                  />
               </Field>

               <Field $col={12} $colMobile={12}>
                  <label
                     htmlFor="upload-is-confidential"
                     style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                     }}
                  >
                     <input
                        id="upload-is-confidential"
                        type="checkbox"
                        name="is_confidential"
                        style={{ width: '16px', height: '16px' }}
                        checked={uploadFormData.is_confidential}
                        onChange={handleUploadFormFieldChange}
                     />
                     Documento confidencial (visível apenas para perfis
                     privilegiados)
                  </label>
               </Field>
            </FormGrid>
            <div
               style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  marginTop: '16px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(0,0,0,0.08)',
               }}
            >
               <button
                  type="button"
                  onClick={handleCloseUploadModal}
                  disabled={isUploading}
               >
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={handleUploadFormSubmit}
                  disabled={isUploading}
               >
                  {isUploading ? 'Enviando...' : 'Enviar documento'}
               </button>
            </div>
         </Modal>

         {/* ─── Modal de confirmação de exclusão ────────────── */}
         <Modal
            isOpen={Boolean(documentPendingDeletion)}
            title="Confirmar exclusão"
            onClose={handleCancelDeleteDocument}
            maxWidth="440px"
         >
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151' }}>
               Tem certeza que deseja excluir o documento{' '}
               <strong>{documentPendingDeletion?.document_title}</strong>?
            </p>
            <p
               style={{
                  margin: '8px 0 0',
                  fontSize: '0.8rem',
                  color: '#9ca3af',
               }}
            >
               Esta ação remove o arquivo do S3 e do banco de dados e não pode
               ser desfeita.
            </p>
            <div>
               <button
                  type="button"
                  onClick={handleCancelDeleteDocument}
                  disabled={isDeleting}
               >
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={handleConfirmDeleteDocument}
                  disabled={isDeleting}
               >
                  {isDeleting ? 'Excluindo...' : 'Confirmar exclusão'}
               </button>
            </div>
         </Modal>
      </>
   );
}

EmployeeDocumentsSection.propTypes = {
   employeeId: PropTypes.string.isRequired,
};
